/**
 * Trunnion AI - Auth Hook
 * Handles login, logout, and token management.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import apiClient from '@/api/client';
import { TOKEN_STORAGE_KEY, REFRESH_TOKEN_STORAGE_KEY } from '@/config/constants';
import { ROUTES } from '@/config/routes';
import type { LoginRequest, LoginResponse } from '@/types';

export function useAuth() {
  const navigate = useNavigate();
  const { user, token, clearanceContext, setAuth, clearAuth, isAuthenticated } =
    useAuthStore();

  const login = useCallback(
    async (credentials: LoginRequest) => {
      const { data } = await apiClient.post<LoginResponse>(
        '/auth/login',
        credentials,
      );

      localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.refreshToken);

      setAuth(data.operator, data.token, data.clearanceContext);
      navigate(ROUTES.DASHBOARD);
    },
    [setAuth, navigate],
  );

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Ignore logout errors
    } finally {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      clearAuth();
      navigate(ROUTES.LOGIN);
    }
  }, [clearAuth, navigate]);

  const refreshSession = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (!refreshToken) return false;

    try {
      const { data } = await apiClient.post<LoginResponse>('/auth/refresh', {
        refreshToken,
      });

      localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.refreshToken);
      setAuth(data.operator, data.token, data.clearanceContext);
      return true;
    } catch {
      clearAuth();
      return false;
    }
  }, [setAuth, clearAuth]);

  return {
    user,
    token,
    clearanceContext,
    isAuthenticated,
    login,
    logout,
    refreshSession,
  };
}
